<script setup lang="ts">
import lowerCase from 'lodash/lowerCase';

import { BadgeColor, BadgeSize, BadgeVariant } from '@/hooks/useBadgeClasses';
import { ButtonColor, ButtonSize, ButtonVariant } from '@/hooks/useButtonClasses';
import { useAuth } from '@/hooks/useAuth';

import Alert from '@/components/alert/Alert.vue';
import Badge from '@/components/badge/Badge.vue';
import Button from '@/components/button/Button.vue';
import Divider from '@/components/divider/Divider.vue';
import Fieldset from '@/components/fieldset/Fieldset.vue';
import IconButton from '@/components/button/IconButton.vue';
import Slideout from '@/components/slideout/Slideout.vue';

import TimesIcon from '@/icons/line/times.svg';

const { show, closeLocally } = useDialog();
const { hasPermission } = useAuth();

const props = defineProps<{
  dataRequest: App.DataRequests.Data.DataRequestData;
  hasErrors?: boolean;
  isApproveLoading?: boolean;
  isDirty?: boolean;
  isRejectLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'approveDataRequest'): void;
  (e: 'rejectDataRequest'): void;
}>();

const computedTitle = computed(() => {
  const prefix = props.dataRequest.type.value === 'create' ? 'Add' : 'Edit';
  return `${prefix} ${props.dataRequest.requestable_type?.label ?? 'Data Request'}`;
});

const computedStatusBadgeColour = computed(() => ({
  pending: BadgeColor.secondary,
  approved: BadgeColor.success,
  rejected: BadgeColor.danger
}));

function rejectDataRequest() {
  emit('rejectDataRequest');
}

function approveDataRequest() {
  emit('approveDataRequest');
}
</script>

<template>
  <Slideout :isOpen="show" @onClose="closeLocally">
    <template #content>
      <div class="flex h-full flex-col">
        <div class="relative flex gap-x-2 border-b border-slate-200 bg-white p-4 pt-6">
          <h1 class="text-base font-semibold leading-5 text-slate-900">{{ computedTitle }}</h1>
          <Badge
            :color="computedStatusBadgeColour[dataRequest.status.value]"
            :label="dataRequest.status.label"
            :size="BadgeSize.sm"
            :variant="BadgeVariant.soft"
          />
          <IconButton
            :size="ButtonSize.md"
            :variant="ButtonVariant.invisible"
            :color="ButtonColor.slate"
            :icon="TimesIcon"
            ariaLabel="Close"
            class="absolute right-4 top-1/2 -translate-y-1/2"
            @click="closeLocally"
          />
        </div>
        <div class="flex grow flex-col gap-5 overflow-y-auto px-4 py-5">
          <Fieldset title="Student Information">
            <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p class="text-sm font-medium leading-5 text-slate-800">
                {{ dataRequest.user?.name }}
              </p>
              <p class="text-sm font-normal leading-3 text-slate-600">
                {{ dataRequest?.school?.name }} - {{ dataRequest?.student_grade?.label }}
              </p>
            </div>
          </Fieldset>
          <Divider />
          <slot />
        </div>
        <div class="bg-white">
          <div
            v-if="dataRequest.user_comment || dataRequest.rejected_reason"
            class="flex flex-col gap-5 border-t border-slate-200 px-4 py-5"
          >
            <Fieldset v-if="dataRequest.user_comment" title="Note from student">
              <p class="text-sm font-normal leading-5 text-slate-700">
                {{ dataRequest.user_comment }}
              </p>
            </Fieldset>
            <Fieldset v-if="dataRequest.rejected_reason" title="Rejected reason">
              <p class="text-sm font-normal leading-5 text-slate-700">
                {{ dataRequest.rejected_reason }}
              </p>
            </Fieldset>
            <Fieldset v-if="false" title="Uploaded Documents">
              <p class="text-sm font-normal leading-5 text-slate-700">No documents uploaded</p>
            </Fieldset>
          </div>
          <div
            v-if="
              dataRequest.status.value === 'pending' &&
              (hasPermission('reject-data-request') || hasPermission('approve-data-request'))
            "
            class="flex flex-col gap-4 border-t border-slate-200 px-4 pb-6 pt-4"
          >
            <Alert
              alignment="vertical"
              :title="`${dataRequest.user?.name} has requested to ${
                dataRequest.type.value === 'create' ? 'add' : 'update'
              } a ${lowerCase(
                dataRequest.requestable_type?.label
              )}. You may edit the data before saving it to the student record.`"
              variant="soft"
              color="secondary"
              hideClose
            />
            <div class="flex justify-end gap-2">
              <Button
                v-if="hasPermission('reject-data-request')"
                :color="ButtonColor.primary"
                :isLoading="isRejectLoading"
                :size="ButtonSize.lg"
                :variant="ButtonVariant.outlined"
                @click="rejectDataRequest"
              >
                Reject data request
              </Button>
              <Button
                v-if="hasPermission('approve-data-request')"
                :color="ButtonColor.slate"
                :isDisabled="hasErrors && !isDirty"
                :isLoading="isApproveLoading"
                :size="ButtonSize.lg"
                @click="approveDataRequest"
              >
                {{
                  hasErrors && !isDirty
                    ? 'Fix validation errors first'
                    : `${dataRequest.type.value === 'create' ? 'Add' : 'Update'} ${
                        dataRequest.requestable_type?.label ?? 'Data Request'
                      }`
                }}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Slideout>
</template>
